export type SoundTone = "chime" | "ping" | "pop" | "ringtone";

export const SOUND_TONE_OPTIONS: { value: SoundTone; label: string }[] = [
  { value: "ringtone", label: "Ringtone" },
  { value: "chime", label: "Chime" },
  { value: "ping", label: "Ping" },
  { value: "pop", label: "Pop" },
];

/** Check if current time falls within the DND window */
export function isDndActive(dndEnabled: boolean, dndStart: string, dndEnd: string): boolean {
  if (!dndEnabled) return false;

  const now = new Date();
  const hhmm = now.getHours() * 60 + now.getMinutes();

  const [sh, sm] = dndStart.split(":").map(Number);
  const [eh, em] = dndEnd.split(":").map(Number);
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;

  // Handles overnight ranges like 22:00 → 08:00
  if (startMin <= endMin) {
    return hhmm >= startMin && hhmm < endMin;
  }
  return hhmm >= startMin || hhmm < endMin;
}

/* ── Singleton AudioContext ── */

let audioCtx: AudioContext | null = null;
let warmUpRegistered = false;
let lastPlayedAt = 0;

type UnlockListener = (unlocked: boolean) => void;
const unlockListeners = new Set<UnlockListener>();

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  if (!audioCtx) {
    try {
      audioCtx = new Ctor();
    } catch {
      return null;
    }
  }
  return audioCtx;
}

/* ── Persisted user grant (survives page refresh) ── */

const GRANT_KEY = "notif_sound_unlocked";

/**
 * Account-level mirror of the grant. Some contexts (embedded previews,
 * private windows, cleared storage) silently drop localStorage writes, so the
 * server-side preference is treated as an equally valid source of truth.
 */
let remoteGrant = false;

/** Feed the account preference (`notification_preferences.sound_unlocked`) in. */
export function setRemoteSoundGrant(granted: boolean) {
  remoteGrant = granted;
  if (granted) markSoundGranted();
}

/** True when the user has previously tapped "Enable sound". */
export function hasUserGrantedSound(): boolean {
  if (remoteGrant) return true;
  try {
    return localStorage.getItem(GRANT_KEY) === "1";
  } catch {
    return false;
  }
}

/** Remember that the user allowed notification sound on this device. */
export function markSoundGranted() {
  try {
    localStorage.setItem(GRANT_KEY, "1");
  } catch {
    /* storage unavailable */
  }
}

/** Forget the grant (e.g. user turned notification sound off). */
export function clearSoundGrant() {
  remoteGrant = false;
  try {
    localStorage.removeItem(GRANT_KEY);
  } catch {
    /* storage unavailable */
  }
}


/** True when the browser will actually let us make sound right now. */
export function isAudioUnlocked(): boolean {
  return !!audioCtx && audioCtx.state === "running";
}


/** Subscribe to unlock-state changes (used by the enable-sound prompt). */
export function onAudioUnlockChange(listener: UnlockListener): () => void {
  unlockListeners.add(listener);
  listener(isAudioUnlocked());
  return () => unlockListeners.delete(listener);
}

function emitUnlockState() {
  const unlocked = isAudioUnlocked();
  unlockListeners.forEach((l) => {
    try {
      l(unlocked);
    } catch {
      /* listener errors are not our problem */
    }
  });
}

/** Play an inaudible blip — required by iOS to fully unlock the context. */
function primeSilently(ctx: AudioContext) {
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.02);
  } catch {
    /* ignore */
  }
}

/**
 * Attempt to unlock audio. Must be called from inside a real user gesture
 * (click / touch / keydown) on iOS Safari and Android Chrome.
 * Safe to call repeatedly.
 */
export async function unlockAudio(): Promise<boolean> {
  const ctx = getAudioContext();
  if (!ctx) return false;
  try {
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
    if (ctx.state === "running") {
      primeSilently(ctx);
    }
  } catch {
    /* still locked */
  }
  emitUnlockState();
  const ok = isAudioUnlocked();
  if (ok) void flushPendingSound();
  return ok;
}

/* ── Pending sound (a notification arrived while audio was locked) ── */

let pendingTone: SoundTone | null = null;

async function flushPendingSound() {
  const tone = pendingTone;
  pendingTone = null;
  if (!tone) return;
  lastPlayedAt = 0;
  await playNotificationSound(tone);
}


/**
 * Register gesture listeners that keep trying to unlock audio until the
 * context actually reports "running". Unlike a one-shot listener, this
 * survives a first gesture that the browser refused.
 */
export function warmUpAudio() {
  if (warmUpRegistered || typeof document === "undefined") return;
  warmUpRegistered = true;

  const events: (keyof DocumentEventMap)[] = ["click", "touchend", "touchstart", "keydown", "pointerdown"];

  const handler = () => {
    void unlockAudio().then((ok) => {
      // Keep listening when the user has granted sound, so the context is
      // silently resumed again after a refresh or a mobile suspend.
      if (ok && !hasUserGrantedSound()) detach();
    });
  };


  const detach = () => {
    events.forEach((e) => document.removeEventListener(e, handler));
  };

  events.forEach((e) => document.addEventListener(e, handler, { passive: true }));

  // Re-check when the tab becomes visible again (mobile suspends contexts)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && audioCtx && audioCtx.state !== "running") {
      void unlockAudio();
    }
  });
}

/** Synthesize notification sounds via Web Audio API */
export async function playNotificationSound(tone: SoundTone = "chime") {
  try {
    // Collapse bursts: a flood of notifications rings once, not ten times.
    const now = Date.now();
    if (now - lastPlayedAt < 1200) return;
    lastPlayedAt = now;

    const ctx = getAudioContext();
    if (!ctx) return;

    // Resume if suspended (safety net for edge cases)
    if (ctx.state === "suspended") {
      await ctx.resume();
      emitUnlockState();
    }
    if (ctx.state !== "running") {
      // Audio still blocked — remember the tone and ring on the next gesture.
      pendingTone = tone;
      lastPlayedAt = 0;
      return;
    }


    const start0 = ctx.currentTime;

    const playTone = (freq: number, start: number, dur: number, type: OscillatorType = "sine", vol = 0.3) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, start0);
      gain.gain.setValueAtTime(vol, start0 + start);
      gain.gain.exponentialRampToValueAtTime(0.001, start0 + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start0 + start);
      osc.stop(start0 + start + dur);
    };

    switch (tone) {
      case "chime":
        playTone(880, 0, 0.15);        // A5
        playTone(1174.66, 0.12, 0.2);   // D6
        break;
      case "ping":
        playTone(1200, 0, 0.08, "sine", 0.25);
        playTone(1600, 0.06, 0.12, "sine", 0.2);
        playTone(2000, 0.14, 0.15, "sine", 0.15);
        break;
      case "pop":
        playTone(400, 0, 0.06, "square", 0.15);
        playTone(800, 0.04, 0.1, "sine", 0.25);
        break;
      case "ringtone": {
        // Classic two-tone ring, repeated — ~2s total
        const pattern = [0, 0.5, 1.0, 1.5];
        for (const start of pattern) {
          playTone(1046.5, start, 0.18, "sine", 0.3);       // C6
          playTone(1318.5, start + 0.16, 0.22, "sine", 0.3); // E6
        }
        break;
      }
    }

    // Short haptic buzz on supported mobile devices
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate(tone === "ringtone" ? [120, 80, 120] : 80);
      }
    } catch {
      /* vibration unsupported */
    }
  } catch {
    // Web Audio not available
  }
}

/** Bypasses the burst throttle — used by the "Test sound" button. */
export async function playNotificationSoundNow(tone: SoundTone = "chime") {
  lastPlayedAt = 0;
  await unlockAudio();
  lastPlayedAt = 0;
  await playNotificationSound(tone);
}
